// Şub 27, 2026 — DPD Subco Karşılaştırma
// Fast-Trans vs diğer subco'ların retour metrikleri
import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { GitCompare, RefreshCw, Loader2, TrendingUp, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { toast } from 'sonner';

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function DPDSubcoComparison() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dateFrom, setDateFrom] = useState(daysAgoIso(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (!firmId || !dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/dpd/subco-comparison?firm_id=${firmId}&date_from=${dateFrom}&date_to=${dateTo}`
      );
      setData(r.data?.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Karşılaştırma yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchTrend = useCallback(async () => {
    if (!firmId) return;
    try {
      const r = await api.get(`/dpd/subco-trend?firm_id=${firmId}&weeks=8`);
      setTrend(r.data?.data);
    } catch (e) { /* ignore */ }
  }, [firmId]);
  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  const rows = data?.rows || [];
  const ft = data?.fast_trans_extra;
  const totalRetour = data?.total_retour || 0;
  const maxCount = rows.reduce((m, r) => Math.max(m, r.retour_count), 0);

  const setPreset = (n) => {
    setDateFrom(daysAgoIso(n));
    setDateTo(todayIso());
  };

  const toggleRow = (sc) => setExpanded((e) => ({ ...e, [sc]: !e[sc] }));

  return (
    <div className="space-y-4" data-testid="dpd-subco-comparison-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-purple-600" />
            {t('dpd.subcoComparison.title')}
          </CardTitle>
          <CardDescription>
            {t('dpd.subcoComparison.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="sc-firm-select"><SelectValue placeholder="Firma" /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="sc-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="sc-date-to" />
            <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="sc-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Yenile
            </Button>
          </div>

          <div className="flex gap-2 mb-4 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setPreset(7)}>Son 7 gün</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(30)}>Son 30 gün</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(90)}>Son 90 gün</Button>
          </div>

          {/* Fast-Trans özet kutucuğu */}
          {ft && (
            <div className="rounded-lg border-2 border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="h-5 w-5 text-emerald-600" />
                <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">
                  {t('dpd.subcoComparison.ftBlockTitle', { days: data?.days || 0 })}
                </h4>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricBlock label={t('dpd.subcoComparison.metrics.delivered')} value={ft.delivered_parcels?.toLocaleString()} />
                <MetricBlock label={t('dpd.subcoComparison.metrics.retour')} value={ft.retour?.toLocaleString()} />
                <MetricBlock label={t('dpd.subcoComparison.metrics.success')} value={`%${ft.success_rate}`} highlight />
                <MetricBlock label={t('dpd.subcoComparison.metrics.totalKm')} value={`${ft.total_km?.toLocaleString()} km`} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatBox label={t('dpd.subcoComparison.stats.totalRetour')} value={totalRetour.toLocaleString()} />
            <StatBox label={t('dpd.subcoComparison.stats.activeSubco')} value={rows.length} />
            <StatBox label={t('dpd.subcoComparison.stats.activeDays')} value={data?.days || 0} />
          </div>

          {/* Weekly trend chart - top 6 subcos */}
          {trend?.subcos && Object.keys(trend.subcos).length > 0 && (
            <div className="mb-4 border rounded-lg p-3" data-testid="subco-trend-chart">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-1">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                  {t('dpd.subcoComparison.trendTitle')}
                </h3>
                <span className="text-[11px] text-gray-500">{trend.date_from} → {trend.date_to}</span>
              </div>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={buildTrendMerged(trend)} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {topSubcos(trend, 6).map((sc, i) => (
                      <Line
                        key={sc}
                        type="monotone"
                        dataKey={sc}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={/fast.?trans/i.test(sc) ? 3 : 2}
                        dot={{ r: 2 }}
                        name={sc}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="rounded-lg border overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[20px]"></TableHead>
                  <TableHead>Subco</TableHead>
                  <TableHead className="text-right w-[80px]">Retour</TableHead>
                  <TableHead className="text-right w-[70px]">Pay %</TableHead>
                  <TableHead>Grafik</TableHead>
                  <TableHead className="text-right w-[70px]">Turlar</TableHead>
                  <TableHead className="text-right w-[80px]">Aktif Gün</TableHead>
                  <TableHead className="text-right w-[90px]">Ort/Gün</TableHead>
                  <TableHead className="text-right w-[80px]">Ağırlık (kg)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-3 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-8">
                    {t('dpd.subcoComparison.empty')}
                  </TableCell></TableRow>
                )}
                {!loading && rows.map((r) => {
                  const isExpanded = !!expanded[r.subco];
                  const pct = Math.round((r.retour_count / Math.max(maxCount, 1)) * 100);
                  return (
                    <Fragment key={r.subco}>
                      <TableRow
                        className={r.is_fast_trans ? 'bg-emerald-50/50 dark:bg-emerald-900/10 cursor-pointer' : 'cursor-pointer'}
                        onClick={() => toggleRow(r.subco)}
                        data-testid={`subco-row-${r.subco}`}
                      >
                        <TableCell>
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{r.subco}</span>
                            {r.is_fast_trans && <Badge className="bg-emerald-600">{t('dpd.common.ownCompany')}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{r.retour_count}</TableCell>
                        <TableCell className="text-right">%{r.share_pct}</TableCell>
                        <TableCell>
                          <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-3">
                            <div
                              className={`h-3 rounded-full ${r.is_fast_trans ? 'bg-emerald-500' : 'bg-red-400'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{r.unique_tours}</TableCell>
                        <TableCell className="text-right">{r.active_days}</TableCell>
                        <TableCell className="text-right">{r.avg_daily_retour}</TableCell>
                        <TableCell className="text-right">{r.total_weight_kg}</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow className="bg-gray-50 dark:bg-gray-900">
                          <TableCell colSpan={9}>
                            <div className="p-2">
                              <div className="text-[11px] font-semibold mb-1 text-gray-700 dark:text-gray-300">
                                {t('dpd.subcoComparison.rootCauses')}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {r.top_reasons.map((rn) => (
                                  <div key={rn.code} className="flex items-center gap-1 bg-white dark:bg-gray-800 border rounded px-2 py-1">
                                    <Badge variant="outline" className="font-mono text-[10px]">{rn.code || '—'}</Badge>
                                    <span className="text-[11px]">{rn.label}</span>
                                    <Badge variant="secondary" className="text-[10px]">{rn.count}</Badge>
                                  </div>
                                ))}
                                {r.top_reasons.length === 0 && (
                                  <span className="text-[11px] text-gray-500">Kök neden verisi yok.</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            {t('dpd.subcoComparison.hint')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricBlock({ label, value, highlight }) {
  return (
    <div>
      <div className="text-xs text-emerald-700 dark:text-emerald-300 opacity-80">{label}</div>
      <div className={`text-2xl font-bold ${highlight ? 'text-emerald-700 dark:text-emerald-200' : ''}`}>{value}</div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3">
      <div className="text-xs text-gray-600 dark:text-gray-400">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

const CHART_COLORS = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'];

function topSubcos(trend, n) {
  const totals = {};
  Object.entries(trend.subcos || {}).forEach(([sc, rows]) => {
    totals[sc] = rows.reduce((s, r) => s + (r.retour || 0), 0);
  });
  return Object.keys(totals).sort((a, b) => totals[b] - totals[a]).slice(0, n);
}

function buildTrendMerged(trend) {
  const top = topSubcos(trend, 6);
  const weeks = new Set();
  top.forEach((sc) => (trend.subcos[sc] || []).forEach((r) => weeks.add(r.week)));
  const wkArr = [...weeks].sort();
  return wkArr.map((wk) => {
    const row = { week: wk };
    top.forEach((sc) => {
      const rec = (trend.subcos[sc] || []).find((r) => r.week === wk);
      row[sc] = rec ? rec.retour : 0;
    });
    return row;
  });
}
